'use client';

import { useEffect, useRef, useState } from 'react';
import { RotateCw, ChevronLeft, ChevronRight } from 'lucide-react';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': any;
    }
  }
}

interface Asset3DProps {
  glbUrl: string; // For model-viewer (all platforms)
  usdzUrl?: string; // Optional: For iOS AR Quick Look (if available)
  assetId: string;
  placement: 'center';
  montessoriMode?: boolean;
  isPlaying?: boolean;
  objectSfxUrl?: string; // Optional: Sound effect URL for object rotation
  isFirstAssetPage?: boolean; // Only show rotation hint on first 3D asset page
}

// ✅ REMOVED: Global import removed to prevent double-import race condition
// Library is now loaded ONLY inside component useEffect with proper state management

export default function Asset3DRenderer({
  glbUrl,
  usdzUrl,
  assetId,
  placement,
  montessoriMode = false,
  isPlaying = true,
  objectSfxUrl,
  isFirstAssetPage = false,
}: Asset3DProps) {
  const [isVisible, setIsVisible] = useState(true); // ✅ FIXED: Start visible so rotation is immediate
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showRotationHint, setShowRotationHint] = useState(isFirstAssetPage); // Only show on first asset page
  const [hasInteracted, setHasInteracted] = useState(false);
  const [currentYaw, setCurrentYaw] = useState(0);
  const [isMobile, setIsMobile] = useState(false); // SSR-safe mobile detection
  const [isModelViewerLoaded, setIsModelViewerLoaded] = useState(false);
  const modelViewerRef = useRef<any>(null);

  // Load model-viewer library only on client side (avoid SSR issues)
  // ✅ CRITICAL FIX: Check if already loaded before importing (prevents double-import race condition)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Check if model-viewer custom element is already registered
      if (customElements.get('model-viewer')) {
        // Already loaded from cache or previous component
        setIsModelViewerLoaded(true);
        console.log('[3D Asset] ✅ model-viewer already loaded (from cache)');
        return;
      }
      
      // Not loaded yet, import it now
      console.log('[3D Asset] Loading model-viewer library with PBR extension...');
      Promise.all([
        import('@google/model-viewer'),
        // ✅ FIX: Load PBR Specular Glossiness extension for correct materials/colors
        import('@google/model-viewer/lib/features/loading').catch((err) => {
          console.warn('[3D Asset] ⚠️ PBR extension load failed (may affect colors):', err);
        }),
      ]).then(() => {
        setIsModelViewerLoaded(true);
        console.log('[3D Asset] ✅ model-viewer loaded with PBR extension support');
      }).catch(err => {
        console.error('[3D Asset] ❌ Failed to load model-viewer:', err);
      });
    }
  }, []);

  // 🔍 DEBUG: Log component props on mount and when key props change
  useEffect(() => {
    console.log('[3D Asset Debug] Component initialized:', {
      assetId,
      glbUrl: glbUrl?.substring(0, 80) + '...',
      usdzUrl: usdzUrl?.substring(0, 80) + '...',
      placement,
      montessoriMode,
      isPlaying,
      isFirstAssetPage,
      isModelViewerLoaded,
    });
  }, [assetId, glbUrl, isModelViewerLoaded]);

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    
    // Detect iOS - safely check navigator
    const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);
    
    // Detect mobile viewport
    setIsMobile(window.innerWidth < 768);
    
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };
    
    // Handle window resize for responsive mobile detection
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    mediaQuery.addEventListener('change', handleChange);
    window.addEventListener('resize', handleResize);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // ✅ REMOVED: No longer needed - isVisible starts as true
  // Objects are now immediately visible with rotation active
  
  // FIX: Explicitly set src attribute on client-side for model-viewer web component
  // This solves SSR hydration issues where src attribute doesn't get applied properly
  useEffect(() => {
    // CRITICAL: Wait for model-viewer library to load before setting src
    if (!isModelViewerLoaded) {
      // Waiting for library...
      return;
    }
    
    if (modelViewerRef.current && glbUrl) {
      // 🎯 PERFORMANCE: Mark start of 3D asset load
      performance.mark('3d-asset-start');
      
      // CRITICAL: Explicitly set the src attribute on the client side
      // This ensures model-viewer web component receives the src after hydration
      modelViewerRef.current.src = glbUrl;
      
      const onLoad = () => {
        // 🎯 PERFORMANCE: Measure load time
        performance.mark('3d-asset-loaded');
        performance.measure('3d-asset-load-time', '3d-asset-start', '3d-asset-loaded');
        
        const measure = performance.getEntriesByName('3d-asset-load-time')[0];
        const loadTime = measure?.duration || 0;
        
        // Log all loads for debugging (not just slow ones)
        if (loadTime > 250) {
          console.warn(`[3D Perf] ⚠️ Slow load (${loadTime.toFixed(0)}ms):`, assetId);
        } else {
          console.log(`[3D Asset] ✅ Loaded successfully (${loadTime.toFixed(0)}ms): ${assetId}`);
        }
        
        // 🔍 DEBUG: Check model-viewer attributes for color rendering
        if (modelViewerRef.current) {
          const mv = modelViewerRef.current;
          console.log('[3D Asset Debug] Model loaded - checking attributes:', {
            assetId,
            src: mv.src?.substring(0, 60),
            exposure: mv.exposure,
            shadowIntensity: mv.shadowIntensity,
            shadowSoftness: mv.shadowSoftness,
            autoRotate: mv.autoRotate,
            cameraOrbit: mv.cameraOrbit,
            environmentImage: mv.environmentImage,
          });
        }
      };
      
      const onError = (e: any) => {
        console.error('[3D Asset] ❌ Model load error:', assetId, e);
        console.error('[3D Asset] Failed URL:', glbUrl);
      };
      
      modelViewerRef.current.addEventListener('load', onLoad);
      modelViewerRef.current.addEventListener('error', onError);
      
      return () => {
        if (modelViewerRef.current) {
          modelViewerRef.current.removeEventListener('load', onLoad);
          modelViewerRef.current.removeEventListener('error', onError);
        }
      };
    } else {
      if (!modelViewerRef.current) {
        console.warn('[3D Asset] ⚠️ modelViewerRef.current is null');
      }
      if (!glbUrl) {
        console.warn('[3D Asset] ⚠️ glbUrl is undefined/null');
      }
    }
  }, [glbUrl, assetId, isModelViewerLoaded]);

  // 🎯 PERFORMANCE: Track when model becomes visible in viewport
  useEffect(() => {
    if (isVisible && isModelViewerLoaded && glbUrl) {
      performance.mark('3d-first-visible');
      // Model is now visible in viewport
    }
  }, [isVisible, isModelViewerLoaded, glbUrl]);

  useEffect(() => {
    // Only set up hint timer if this is the first asset page
    if (!isFirstAssetPage) return;
    
    // Hide hint after 8 seconds or on first interaction
    const hintTimer = setTimeout(() => {
      setShowRotationHint(false);
    }, 8000);

    const handleInteraction = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
        setShowRotationHint(false);
      }
    };

    if (modelViewerRef.current) {
      modelViewerRef.current.addEventListener('camera-change', handleInteraction);
    }

    return () => {
      clearTimeout(hintTimer);
      if (modelViewerRef.current) {
        modelViewerRef.current.removeEventListener('camera-change', handleInteraction);
      }
    };
  }, [hasInteracted, isFirstAssetPage]);

  // Rotation control functions with sound effect
  const rotateLeft = () => {
    if (modelViewerRef.current) {
      const newYaw = currentYaw + 45;
      setCurrentYaw(newYaw);
      modelViewerRef.current.cameraOrbit = `${newYaw}deg 75deg 105%`;
      setHasInteracted(true);
      setShowRotationHint(false);
      
      // Play object sound effect when rotating
      if (objectSfxUrl) {
        const audio = new Audio(objectSfxUrl);
        audio.volume = 0.6; // Slightly lower volume for rotation
        audio.play().catch(err => console.log('Audio play failed:', err));
      }
    }
  };

  const rotateRight = () => {
    if (modelViewerRef.current) {
      const newYaw = currentYaw - 45;
      setCurrentYaw(newYaw);
      modelViewerRef.current.cameraOrbit = `${newYaw}deg 75deg 105%`;
      setHasInteracted(true);
      setShowRotationHint(false);
      
      // Play object sound effect when rotating
      if (objectSfxUrl) {
        const audio = new Audio(objectSfxUrl);
        audio.volume = 0.6; // Slightly lower volume for rotation
        audio.play().catch(err => console.log('Audio play failed:', err));
      }
    }
  };

  // Determine if auto-rotate should be enabled
  // Disable only for reduced motion preference
  const shouldAutoRotate = !prefersReducedMotion && isPlaying;

  // VIEWPORT-BASED sizing - responsive to screen size (set in useEffect)
  const containerSize = isMobile 
    ? 'min(50vw, 350px)'  // Mobile: smaller of 50vw or 350px
    : 'min(35vw, 400px)';  // Desktop: smaller of 35vw or 400px

  // 🐛 DEBUG: Log rotation state (only when it changes)
  useEffect(() => {
    console.log('[3D Auto-Rotate]', {
      shouldAutoRotate,
      prefersReducedMotion,
      isPlaying,
      assetId,
    });
  }, [shouldAutoRotate, prefersReducedMotion, isPlaying, assetId]);

  // Don't render until model-viewer library is loaded (prevents SSR errors)
  if (!isModelViewerLoaded) {
    // Library not loaded yet, returning null
    return (
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: containerSize,
          height: containerSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '14px',
          zIndex: 100,
        }}
      >
        Loading 3D object...
      </div>
    );
  }

  return (
    <>
      {/* Subtle glow effect behind 3D object */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: containerSize,
          height: containerSize,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(168, 216, 230, 0.3) 0%, transparent 70%)',
          filter: 'blur(40px)',
          opacity: isVisible ? 0.8 : 0,
          transition: 'opacity 0.15s ease-in', // Optimized: ≤150ms target
          zIndex: 98,
          pointerEvents: 'none',
        }}
      />
      
      {/* Main 3D object container */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: containerSize,
          height: containerSize,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.15s ease-in', // Optimized: ≤150ms target
          zIndex: 100,
          pointerEvents: 'auto',
          filter: 'drop-shadow(0 12px 32px rgba(0, 0, 0, 0.25))',
        }}
        onTouchStart={(e) => {
          // Mark that we're interacting with 3D object - prevent page swipe
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          // Prevent page swipe while rotating 3D model
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
        }}
      >
        <model-viewer
          ref={modelViewerRef}
          src={glbUrl}
          ios-src={usdzUrl || glbUrl}
          alt={assetId}
          ar={!!usdzUrl}
          ar-modes={usdzUrl ? "webxr scene-viewer quick-look" : undefined}
          auto-rotate={shouldAutoRotate}
          rotation-per-second={shouldAutoRotate ? '40deg' : '0deg'}
          camera-orbit="0deg 75deg 105%"
          environment-image="neutral"
          exposure="1.0"
          shadow-intensity="1"
          shadow-softness="0.8"
          disable-zoom={false}
          interaction-prompt="none"
          loading="eager"
          reveal="auto"
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
            '--poster-color': 'transparent',
            '--progress-bar-color': 'transparent',
          }}
        />
      </div>

      {/* Rotation Control Buttons */}
      <>
        {/* Left Rotation Button */}
        <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              rotateLeft();
            }}
            onPointerDown={(e) => {
              e.stopPropagation(); // Prevent swipe detection
            }}
            onTouchStart={(e) => {
              e.stopPropagation(); // Prevent mobile touch handlers
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
            }}
            style={{
              position: 'absolute',  // FIXED: Changed from 'fixed' to work in split layouts
              left: isMobile ? '5px' : '10px',  // FIXED: Positioned inside the image container
              top: '50%',
              transform: 'translateY(-50%)',
              width: isMobile ? '48px' : '60px',  // FIXED: Smaller on mobile
              height: isMobile ? '48px' : '60px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
              border: '3px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              zIndex: 150,
              opacity: isVisible ? 1 : 0,
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
            }}
          >
            <ChevronLeft size={isMobile ? 24 : 28} strokeWidth={3} />
          </button>

          {/* Right Rotation Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              rotateRight();
            }}
            onPointerDown={(e) => {
              e.stopPropagation(); // Prevent swipe detection
            }}
            onTouchStart={(e) => {
              e.stopPropagation(); // Prevent mobile touch handlers
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
            }}
            style={{
              position: 'absolute',  // FIXED: Changed from 'fixed' to work in split layouts
              right: isMobile ? '5px' : '10px',  // FIXED: Positioned inside the image container
              top: '50%',
              transform: 'translateY(-50%)',
              width: isMobile ? '48px' : '60px',  // FIXED: Smaller on mobile
              height: isMobile ? '48px' : '60px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
              border: '3px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              zIndex: 150,
              opacity: isVisible ? 1 : 0,
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
            }}
          >
            <ChevronRight size={isMobile ? 24 : 28} strokeWidth={3} />
          </button>
      </>

      {/* Rotation hint - top center, single line with icon */}
      {showRotationHint && (
        <div
          style={{
            position: 'absolute',  // FIXED: Changed from 'fixed' for container-relative
            top: isMobile ? '-40px' : '-50px',  // FIXED: Above 3D object
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
            padding: '10px 20px',
            borderRadius: '50px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#fff',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.15s ease-in', // Optimized: ≤150ms target
            zIndex: 200,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
            backdropFilter: 'blur(10px)',
            pointerEvents: 'none',
            animation: isVisible ? 'gentlePulse 2s ease-in-out infinite' : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
          }}
        >
          <RotateCw size={16} strokeWidth={2.5} />
          <span>Use arrows or drag to rotate</span>
        </div>
      )}

      {/* Subtle hint text for AR on iOS (only if USDZ available) */}
      {usdzUrl && isIOS && (
        <div
          style={{
            position: 'absolute',
            bottom: '-30px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '11px',
            color: 'rgba(255, 255, 255, 0.6)',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 1s ease-in 1s',
            whiteSpace: 'nowrap',
          }}
        >
          Tap to view in AR
        </div>
      )}
    </>
  );
}
